import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Percent,
  RefreshCw,
  Search,
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Calculator,
  DollarSign,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const formatAmount = (amount, currency = 'KZT') => {
  if (!amount && amount !== 0) return '—';
  if (currency === 'KZT') {
    return `${amount.toLocaleString('ru-RU')} ₸`;
  }
  return `${amount.toLocaleString('ru-RU')} ${currency}`;
};

const TypeBadge = ({ type }) => {
  const config = {
    percent: { label: 'Процент', className: 'bg-blue-500/20 text-blue-400' },
    fixed: { label: 'Фикс', className: 'bg-purple-500/20 text-purple-400' },
    mixed: { label: 'Смешанный', className: 'bg-amber-500/20 text-amber-400' },
  };
  const c = config[type] || config.percent;
  return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
};

const CommissionsPage = () => {
  const { hasPermission } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0 });
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Create/Edit dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    commission_type: 'percent',
    percent_value: '',
    fixed_value: '',
    min_amount: '',
    max_amount: '',
    merchant_id: '',
    gateway: '',
    currency: 'KZT',
    is_active: true,
  });

  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingRule, setDeletingRule] = useState(null);

  const canWrite = hasPermission('merchants', 'write');

  const fetchStats = async () => {
    try {
      const response = await api.get('/commissions/stats');
      setStats(response.data);
    } catch (err) {
      setStats({
        total_rules: 0,
        active_rules: 0,
        inactive_rules: 0,
        by_type: {},
      });
    }
  };

  const fetchRules = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.pageSize);
      if (search) params.append('search', search);
      if (activeFilter && activeFilter !== 'all') params.append('is_active', activeFilter === 'true');
      
      const response = await api.get(`/commissions?${params}`);
      setRules(response.data.rules || []);
      setPagination({
        page: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      });
    } catch (err) {
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchRules();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRules(1);
  };

  const handleFilterChange = (value) => {
    setActiveFilter(value);
    fetchRules(1);
  };

  // Create/Edit handlers
  const openCreateDialog = () => {
    setEditingRule(null);
    setForm({
      name: '',
      description: '',
      commission_type: 'percent',
      percent_value: '',
      fixed_value: '',
      min_amount: '',
      max_amount: '',
      merchant_id: '',
      gateway: '',
      currency: 'KZT',
      is_active: true,
    });
    setShowDialog(true);
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name || '',
      description: rule.description || '',
      commission_type: rule.commission_type || 'percent',
      percent_value: rule.percent_value?.toString() || '',
      fixed_value: rule.fixed_value?.toString() || '',
      min_amount: rule.min_amount?.toString() || '',
      max_amount: rule.max_amount?.toString() || '',
      merchant_id: rule.merchant_id?.toString() || '',
      gateway: rule.gateway || '',
      currency: rule.currency || 'KZT',
      is_active: rule.is_active ?? true,
    });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error('Введите название правила');
      return;
    }

    const payload = {
      name: form.name,
      description: form.description || null,
      commission_type: form.commission_type,
      percent_value: form.percent_value ? parseFloat(form.percent_value) : null,
      fixed_value: form.fixed_value ? parseFloat(form.fixed_value) : null,
      min_amount: form.min_amount ? parseFloat(form.min_amount) : null,
      max_amount: form.max_amount ? parseFloat(form.max_amount) : null,
      merchant_id: form.merchant_id ? parseInt(form.merchant_id) : null,
      gateway: form.gateway || null,
      currency: form.currency,
      is_active: form.is_active,
    };

    try {
      setSaving(true);
      if (editingRule) {
        await api.put(`/commissions/${editingRule.id}`, payload);
        toast.success('Правило обновлено');
      } else {
        await api.post('/commissions', payload);
        toast.success('Правило создано');
      }
      setShowDialog(false);
      fetchStats();
      fetchRules(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const openDeleteDialog = (rule) => {
    setDeletingRule(rule);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/commissions/${deletingRule.id}`);
      toast.success('Правило удалено');
      setShowDeleteDialog(false);
      fetchStats();
      fetchRules(pagination.page);
    } catch (err) {
      toast.error('Ошибка удаления');
    }
  };

  return (
    <div className="space-y-6" data-testid="commissions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('commissions.title')}</h1>
          <p className="text-slate-400 mt-1">Управление правилами комиссий</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={async () => {
              try {
                const response = await api.get('/export/commissions', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `commissions_${new Date().toISOString().slice(0, 10)}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success('Экспорт завершён');
              } catch (err) {
                toast.error('Ошибка экспорта');
              }
            }}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <Download className="w-4 h-4 mr-1" />
            {t('common.export')}
          </Button>
          {canWrite && (
            <Button
              onClick={openCreateDialog}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="create-commission-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('common.create')}
            </Button>
          )}
          <Button
            onClick={() => { fetchStats(); fetchRules(pagination.page); }}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <p className="text-xs text-slate-400">Всего правил</p>
              <p className="text-xl font-bold text-white">{stats.total_rules}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/10 border-emerald-500/30">
            <CardContent className="p-4">
              <p className="text-xs text-emerald-400">Активные</p>
              <p className="text-xl font-bold text-emerald-400">{stats.active_rules}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <p className="text-xs text-slate-400">Неактивные</p>
              <p className="text-xl font-bold text-slate-400">{stats.inactive_rules}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4">
              <p className="text-xs text-blue-400">Процентные</p>
              <p className="text-xl font-bold text-blue-400">{stats.by_type?.percent || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Поиск по названию..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-slate-700/50 border-slate-600 text-white"
            />
          </div>
          <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600">
            Найти
          </Button>
        </form>
        <Select value={activeFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-40 bg-slate-700/50 border-slate-600 text-white">
            <SelectValue placeholder={t('common.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все</SelectItem>
            <SelectItem value="true">Активные</SelectItem>
            <SelectItem value="false">Неактивные</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Calculator className="w-5 h-5 text-emerald-500" />
            Правила комиссий ({pagination.total})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : rules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Percent className="w-12 h-12 mb-4 opacity-50" />
              <p>Правил комиссий нет</p>
              {canWrite && (
                <Button
                  onClick={openCreateDialog}
                  className="mt-4 bg-emerald-500 hover:bg-emerald-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Создать первое правило
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Название</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('commissions.type')}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('commissions.value')}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Мин/Макс сумма</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('payments.gateway')}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('common.status')}</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <tr
                      key={rule.id}
                      className="border-b border-slate-700/50 hover:bg-slate-700/30"
                      data-testid={`commission-row-${rule.id}`}
                    >
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{rule.name}</p>
                          {rule.description && (
                            <p className="text-slate-400 text-xs mt-1">{rule.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <TypeBadge type={rule.commission_type} />
                      </td>
                      <td className="p-4 text-white">
                        {rule.commission_type === 'percent' && rule.percent_value !== null && (
                          <span>{rule.percent_value}%</span>
                        )}
                        {rule.commission_type === 'fixed' && rule.fixed_value !== null && (
                          <span>{formatAmount(rule.fixed_value, rule.currency)}</span>
                        )}
                        {rule.commission_type === 'mixed' && (
                          <span>
                            {rule.percent_value}% + {formatAmount(rule.fixed_value, rule.currency)}
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-slate-300 text-sm">
                        {rule.min_amount || rule.max_amount ? (
                          <span>
                            {formatAmount(rule.min_amount, rule.currency)} — {formatAmount(rule.max_amount, rule.currency)}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="p-4 text-slate-300">
                        {rule.gateway || '—'}
                      </td>
                      <td className="p-4">
                        <Badge
                          variant="outline"
                          className={rule.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}
                        >
                          {rule.is_active ? 'Активно' : 'Неактивно'}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        {canWrite && (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(rule)}
                              className="text-slate-400 hover:text-white"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(rule)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {Math.ceil(pagination.total / pagination.pageSize) > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Страница {pagination.page} из {Math.ceil(pagination.total / pagination.pageSize)}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchRules(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchRules(pagination.page + 1)}
                  disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-emerald-500" />
              {editingRule ? 'Редактировать правило' : 'Новое правило комиссии'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingRule ? `Редактирование правила "${editingRule.name}"` : 'Заполните данные нового правила'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Название *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Название правила"
                className="bg-slate-700/50 border-slate-600 text-white"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Описание</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Описание правила"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Тип комиссии</Label>
                <Select
                  value={form.commission_type}
                  onValueChange={(value) => setForm({ ...form, commission_type: value })}
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Процент</SelectItem>
                    <SelectItem value="fixed">Фиксированная</SelectItem>
                    <SelectItem value="mixed">Смешанная</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">{t('terminals.currency')}</Label>
                <Select
                  value={form.currency}
                  onValueChange={(value) => setForm({ ...form, currency: value })}
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="KZT">KZT</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="RUB">RUB</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(form.commission_type === 'percent' || form.commission_type === 'mixed') && (
              <div className="space-y-2">
                <Label className="text-slate-300">Процент (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.percent_value}
                  onChange={(e) => setForm({ ...form, percent_value: e.target.value })}
                  placeholder="2.5"
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
            )}

            {(form.commission_type === 'fixed' || form.commission_type === 'mixed') && (
              <div className="space-y-2">
                <Label className="text-slate-300">Фиксированная сумма</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.fixed_value}
                  onChange={(e) => setForm({ ...form, fixed_value: e.target.value })}
                  placeholder="100"
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Мин. сумма</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.min_amount}
                  onChange={(e) => setForm({ ...form, min_amount: e.target.value })}
                  placeholder="1000"
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Макс. сумма</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.max_amount}
                  onChange={(e) => setForm({ ...form, max_amount: e.target.value })}
                  placeholder="1000000"
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">ID Мерчанта</Label>
                <Input
                  type="number"
                  value={form.merchant_id}
                  onChange={(e) => setForm({ ...form, merchant_id: e.target.value })}
                  placeholder="Для всех"
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">{t('payments.gateway')}</Label>
                <Input
                  value={form.gateway}
                  onChange={(e) => setForm({ ...form, gateway: e.target.value })}
                  placeholder="Для всех"
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
              <Label className="text-slate-300">Активно</Label>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="border-slate-600 text-slate-300"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={saving}
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : null}
                {editingRule ? 'Сохранить' : 'Создать'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400">Удалить правило?</DialogTitle>
            <DialogDescription className="text-slate-400">
              Вы уверены, что хотите удалить правило "{deletingRule?.name}"?
              Это действие нельзя отменить.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="border-slate-600 text-slate-300"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommissionsPage;
